import React, { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * Category tab strip under the header (SHEIN / Temu style): one scrolling
 * row of text tabs, the active one bold with a crimson bar under it. The
 * row follows the reading direction, so it flips in Arabic. Uses the six
 * real category slugs plus the sale, brands and collections pages.
 */
export const CATEGORY_TABS = [
  { id: 'all', to: '/products', label: { en: 'All', ar: 'الكل' } },
  { id: 'women', to: '/products?cat=women', label: { en: 'Women', ar: 'نساء' } },
  { id: 'men', to: '/products?cat=men', label: { en: 'Men', ar: 'رجال' } },
  { id: 'bags', to: '/products?cat=bags', label: { en: 'Bags', ar: 'حقائب' } },
  { id: 'jewelry', to: '/products?cat=jewelry', label: { en: 'Jewellery', ar: 'مجوهرات' } },
  { id: 'perfumes', to: '/products?cat=perfumes', label: { en: 'Fragrance', ar: 'عطور' } },
  { id: 'accessories', to: '/products?cat=accessories', label: { en: 'Accessories', ar: 'إكسسوارات' } },
  { id: 'sale', to: '/products?sale=true', label: { en: 'Sale', ar: 'تخفيضات' } },
  { id: 'brands', to: '/brands', label: { en: 'Brands', ar: 'العلامات' } },
  { id: 'collections', to: '/collections', label: { en: 'Collections', ar: 'التشكيلات' } },
];

/** The tab id for a location (null when no tab matches). */
export const activeTabFor = (pathname, search) => {
  const params = new URLSearchParams(search || '');
  if (pathname === '/') return 'all';
  if (pathname.startsWith('/brands')) return 'brands';
  if (pathname.startsWith('/collections')) return 'collections';
  if (pathname !== '/products') return null;
  if (params.get('sale') === 'true' || params.get('on_sale') === 'true') return 'sale';
  const cat = (params.get('cat') || params.get('category') || '').toLowerCase();
  if (cat) return CATEGORY_TABS.some((tab) => tab.id === cat) ? cat : null;
  return params.get('search') ? null : 'all';
};

/** Scrolls the tab's own strip (horizontally) so the tab is visible; the page never moves. */
export const revealTab = (node) => {
  const strip = node && node.closest ? node.closest('ul') : null;
  if (!strip || strip.scrollWidth <= strip.clientWidth) return;
  const s = strip.getBoundingClientRect();
  const n = node.getBoundingClientRect();
  if (n.left < s.left) strip.scrollLeft -= s.left - n.left;
  else if (n.right > s.right) strip.scrollLeft += n.right - s.right;
};

const CategoryTabs = ({ className = '' }) => {
  const { t } = useLanguage();
  const location = useLocation();
  const active = activeTabFor(location.pathname, location.search);
  const activeRef = useRef(null);

  // Keep the active tab in view by scrolling the strip sideways only. Never
  // scrollIntoView: that also scrolls the window, which undid the shop's
  // scroll restore on Back (the page jumped to the top).
  useEffect(() => {
    revealTab(activeRef.current);
  }, [active]);

  return (
    <nav aria-label={t({ en: 'Shop by category', ar: 'تسوق حسب الفئة' })} className={className} data-testid="category-tabs">
      <ul className="anum-no-scrollbar flex snap-x items-stretch gap-1 overflow-x-auto px-2 md:justify-center md:px-4">
        {CATEGORY_TABS.map((tab) => {
          const isActive = tab.id === active;
          return (
            <li key={tab.id} className="shrink-0 snap-start">
              <Link
                ref={isActive ? activeRef : undefined}
                to={tab.to}
                aria-current={isActive ? 'page' : undefined}
                className={`relative flex min-h-[44px] items-center whitespace-nowrap px-3 text-[15px] transition-colors motion-reduce:transition-none ${
                  isActive
                    ? 'font-bold text-foreground'
                    : tab.id === 'sale'
                    ? 'font-semibold text-crimson-ink hover:text-crimson-deep'
                    : 'font-medium text-stone hover:text-foreground'
                }`}
              >
                {t(tab.label)}
                <span
                  aria-hidden="true"
                  className={`absolute inset-x-3 bottom-1 h-[3px] rounded-full bg-crimson transition-transform duration-200 motion-reduce:transition-none ${isActive ? 'scale-x-100' : 'scale-x-0'}`}
                />
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default CategoryTabs;
